import { BillingMethodList } from '@/features/billing/components/BillingMethodList'
import { BillingMethodListItemContainer } from '@/features/billing/components/BillingMethodList/BillingMethodListItemContainer'
import {
  ContentEmpty,
  ContentLayout,
  ContentMain,
  ContentTip,
} from '@/components/ContentLayout'
import {
  DropdownItem,
  DropdownItemDescription,
  DropdownItemTitle,
} from '@/components/Dropdown'
import { ListHeader } from '@/components/ListHeader'
import { observer } from 'mobx-react-lite'
import { type TeamPaymentsViewModel } from './TeamPaymentsViewModel'
import { ContentLoading } from '@/components/ContentLoading'
import { ContentError } from '@/components/ContentError'
import { DeleteBillingMethodDialogContainer } from '@/features/billing/payment-methods/DeleteBillingMethodDialog/DeleteBillingMethodDialogContainer'
import { PaymentMethodsEmptyState } from '@/features/billing/components/PaymentMethodsEmptyState'
import { Icon } from '@/components/Icon'
import { byDesign } from '@/infra/design'
import imgWorkspaceBillingUrl from '@/assets/misc/img_workspace-billing.svg'
import WorkspaceBillingIllustration from '@/components/SpotIllustration/WorkspaceBillingIllustration'
import { useFeatures } from '@/contexts/root-store-context'

export interface TeamPaymentsContainerProps {
  vm: TeamPaymentsViewModel
}

export const TeamPaymentsContainer = observer(function TeamPaymentsContainer({
  vm,
}: TeamPaymentsContainerProps) {
  const { shellInfo } = useFeatures(({ shell }) => ({
    shellInfo: shell.shellInfo,
  }))
  return (
    <ContentLayout accountForLabel accountForTabBar>
      {vm.fetch.match({
        pending: () => <ContentLoading />,
        rejected: (err: unknown) => <ContentError error={err} />,
        resolved: () => (
          <>
            {vm.methods.items.length === 0 ? (
              <ContentEmpty>
                <PaymentMethodsEmptyState
                  canAddPaymentMethod={vm.canManageTeamBilling}
                  isAddPending={vm.addPaymentMethod.pending}
                  onNewPaymentMethod={vm.addPaymentMethod}
                  corporateMode={shellInfo.corporateMode}
                />
              </ContentEmpty>
            ) : (
              <>
                <ContentMain>
                  <ListHeader count={vm.methods.items.length}>
                    Payment Methods
                  </ListHeader>
                  <BillingMethodList
                    isAddDisabled={vm.disabled}
                    onAdd={
                      vm.canManageTeamBilling ? vm.addPaymentMethod : undefined
                    }
                  >
                    {vm.methods.items.map((methodVm) => (
                      <BillingMethodListItemContainer
                        key={methodVm.id}
                        vm={methodVm}
                        isDisabled={vm.disabled}
                        onDelete={
                          vm.canManageTeamBilling ? methodVm.delete : undefined
                        }
                        dropdownActions={
                          <>
                            {vm.canManageTeamBilling && (
                              <DropdownItem
                                onPress={methodVm.setAsDefault}
                                isDisabled={vm.disabled || methodVm.isDefault}
                                icon={<Icon name="default-payment" />}
                              >
                                <DropdownItemTitle>
                                  Set as Current
                                </DropdownItemTitle>
                                <DropdownItemDescription>
                                  {methodVm.isDefault ? (
                                    <>
                                      This payment method is already the
                                      default.
                                    </>
                                  ) : (
                                    <>
                                      Future payments charged to this team will
                                      use this payment method.
                                    </>
                                  )}
                                </DropdownItemDescription>
                              </DropdownItem>
                            )}
                          </>
                        }
                      />
                    ))}
                  </BillingMethodList>
                </ContentMain>

                <ContentTip
                  corporateMode={shellInfo.corporateMode}
                  illustrationUrl={imgWorkspaceBillingUrl}
                  illustration={byDesign(
                    undefined,
                    WorkspaceBillingIllustration,
                  )}
                  title="Transactions don't have to be complicated"
                >
                  Are you looking to get more out of your Workspace? Our
                  in-depth Help Center can help!
                </ContentTip>
              </>
            )}
          </>
        ),
      })}
      <DeleteBillingMethodDialogContainer vm={vm.deleteDialogVm} />
    </ContentLayout>
  )
})
